/** @graphql cc **/

import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';
import type { QueryOptions } from '@apollo/client';

import {
	preloadPrefilteredExtensionList,
	type Extension,
	type UsePreFilteredExtensionsQueryOptions,
} from '@atlassian/forge-ui';

import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { transformPreFilteredExtensions } from '../utils/transformForgeExtensions';

import type {
	preloadForgeAppsQuery,
	preloadForgeAppsQueryVariables,
} from './__types__/preloadForgeAppsQuery';
// eslint-disable-next-line import/no-cycle

export const preloadForgeApps = async ({
	cloudId,
	locale,
	types,
	context,
	includeHidden,
}: UsePreFilteredExtensionsQueryOptions): Promise<Extension[] | null> => {
	if (fg('memoize_prefiltered_extensions')) {
		return preloadPrefilteredExtensionList(
			// This is s a special case that the query is from platform in DocumentNode not Relay TaggedNode
			// query can handle both Relay TaggedNode and DocumentNode but type is only declared as Relay TaggedNode
			(expVal('confluence_cgraphql_client_for_forge_extensions', 'isEnabled', false)
				? query
				: queryAgg) as unknown as <T, V>(
				options: Omit<QueryOptions<V>, 'query'> & { query: DocumentNode },
			) => Promise<{ data: T | null }>,
			{
				cloudId,
				locale,
				types,
				context,
				includeHidden,
			},
		);
	} else {
		const { data } = await (
			expVal('confluence_cgraphql_client_for_forge_extensions', 'isEnabled', false)
				? query
				: queryAgg
		)<preloadForgeAppsQuery, preloadForgeAppsQueryVariables>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming
			query: gql`
				query FetchPreFilteredForgeAppsQuery(
					$cloudId: ID!
					$context: ConfluenceExtensionRenderingContextInput
					$includeHidden: Boolean
					$locale: String
					$types: [String]
				) {
					confluence_forgeExtensionsByType(
						cloudId: $cloudId
						context: $context
						includeHidden: $includeHidden
						locale: $locale
						types: $types
					) {
						id
						appId
						appVersion
						appOwner
						consentUrl
						environmentId
						environmentKey
						environmentType
						migrationKey
						key
						properties {
							key
							value
						}
						egress {
							addresses
							category
							type
							inScopeEUD
						}
						scopes
						license {
							active
							capabilitySet
						}
						userAccess {
							enabled
							hasAccess
						}
						type
						installationId
						installationConfig {
							key
							value
						}
						hiddenBy
					}
				}
			`,
			variables: {
				cloudId,
				context,
				includeHidden: includeHidden ?? false,
				types,
				locale,
			} as preloadForgeAppsQueryVariables,
		});

		return transformForgeApps(data);
	}
};

const transformForgeApps = (data: preloadForgeAppsQuery | null): Extension[] | null => {
	if (!data || !data.confluence_forgeExtensionsByType) {
		return null;
	}

	return transformPreFilteredExtensions(data.confluence_forgeExtensionsByType);
};
